// ════════════════════════════════════════════════════════════════════════════
//  ManDana — the record model
//  ---------------------------------------------------------------------------
//  What a record IS, as text: which part of it is the title, which part is the
//  body, and whether anything has been written at all. No DOM, no React, no
//  store — md-compose.jsx writes records, md-today.jsx and md-grid.jsx show them,
//  and md-app.jsx decides when one lives or dies. All three ask this.
//
//  WHY IT EXISTS. A record is created the moment it is opened now, the way Apple
//  Notes does it: tapping "+" puts a card in the day immediately, the card's
//  title follows the first line as it is typed, and a record closed with nothing
//  in it is dropped again. Three files have to agree on the answers or the flow
//  breaks in ways nobody would notice until a stray tap left a blank card in
//  someone's day forever — mdRecordIsBlank is the one that stands between those
//  two, so it is the one that had to be provable. See tests/record.test.js.
//
//  BLANK IS NOT "NO CHARACTERS". `# ` is a heading nobody has written yet: it
//  names nothing and says nothing, so it must not hold a card open. Blankness
//  is therefore asked of the PARTS — no name and no body — not of the raw text,
//  and it agrees by construction with what the day list would have to show.
//
//  ── A RECORD IS A UNIT OF WRITING AND IDENTITY, NOT A UNIT OF STORAGE ──────
//  Everything written in this app is a record: what happened on a Tuesday, a
//  project's vision, a domain's picture of who you want to be there. One editor,
//  one card, one title rule, at every altitude.
//
//  What differs is WHERE ONE LIVES, and that difference is real rather than
//  cosmetic. A day record is an element inside `md:events:{day}` — a document
//  that merges as a unit and tombstones as a unit. A project and a domain have
//  no day, so each gets its own document, the way a board does. Forcing them
//  into a day document would mean inventing a day for a thing that has none,
//  and dropping it into the one shape the sync deletion trap lives in (see the
//  header of md-board.ts).
//
//  So: ONE SPECIES, TWO HOMES. An MdRecordRef is what names one whatever its
//  home, and it is the whole reason a board, a table or a dashboard can hold
//  "a record" without learning three kinds of thing.
//
//  TYPE SAYS WHAT IT IS; `pid` SAYS WHAT IT BELONGS TO. They are deliberately
//  not the same field. A type field cannot enforce a hierarchy — a pointer does
//  — and merging the two is how you end up with a `parent` on everything and a
//  tree that can hold cycles. There is exactly one upward pointer in this app,
//  and the domain is derived from it (see mdProjectDomainFor).
//
//  AN UNKNOWN TYPE IS NOT A BROKEN ONE. A well-formed ref naming a type this
//  build has never heard of round-trips untouched, so a newer client's board
//  survives being opened on an older one. Same bias as md-sync.js: repair, not
//  reject. Only malformed refs are refused.
// ════════════════════════════════════════════════════════════════════════════

/** A record split the way it is stored: line one is the title, the rest is the record. */
interface MdRecordParts {
  name: string;
  body: string;
}

/** What names one record, wherever it lives. `day` is present only for a record
    anchored to a day; a project or a domain has none. */
interface MdRecordRef {
  type: string;
  id: string;
  day?: string;
}

(function (g: any) {
  'use strict';

  /* What an untitled record is called wherever it is listed. It is a real title
     in no store — a record with no first line has `name: ''` — so nothing has to
     be un-said later when the writer types one. */
  const MD_RECORD_PLACEHOLDER = 'New Record';

  /* The markers a writer starts a line with. Stripped from the TITLE only: the
     body keeps its own syntax, because the body is still markdown. */
  const MD_RECORD_MARKERS = /^[#>\-*\s]+/;

  /* A title is a label in a list, not a paragraph. 60 characters is what the
     card can show; the whole line stays in the body text either way. */
  const MD_RECORD_NAME_MAX = 60;

  /** Split a record's text into the title and the body beneath it. */
  function mdRecordParts(text: unknown): MdRecordParts {
    if (typeof text !== 'string' || !text) return { name: '', body: '' };
    const lines = text.split('\n');
    /* Leading blank lines must not leave a record nameless — the first line that
       says something wins. The BODY still starts after line 1 regardless, which
       is what keeps the writer's own spacing where they put it. */
    const first = lines[0] || '';
    const nameLine = first.trim() ? first : lines.find((l) => l.trim()) || '';
    const rest = lines.slice(1).join('\n');
    return {
      name: nameLine.replace(MD_RECORD_MARKERS, '').trim().slice(0, MD_RECORD_NAME_MAX),
      body: rest.trim() ? rest : '',
    };
  }

  /** Has anything actually been written here? */
  function mdRecordIsBlank(text: unknown): boolean {
    const p = mdRecordParts(text);
    return !p.name && !p.body.trim();
  }

  /** What a list shows for a record — its title, or the placeholder until it has one. */
  function mdRecordTitle(rec: unknown): string {
    const name = rec && typeof rec === 'object' ? (rec as { name?: unknown }).name : null;
    return typeof name === 'string' && name.trim() ? name : MD_RECORD_PLACEHOLDER;
  }

  /** Put a stored record back into the one text the editor writes.

      The de-duplication is history, not tidiness: capture-first-era records saved
      the name AND repeated it as the body's first line, so seeding them naively
      shows every one of those records with its title written twice. */
  function mdRecordSeedText(rec: unknown): string {
    if (!rec || typeof rec !== 'object') return '';
    const r = rec as { name?: unknown; body?: unknown };
    const name = (typeof r.name === 'string' ? r.name : '').trim();
    const body = typeof r.body === 'string' ? r.body : '';
    const first = (body.split('\n')[0] || '').replace(MD_RECORD_MARKERS, '').trim();
    if (name && first && (first === name || first.slice(0, MD_RECORD_NAME_MAX) === name)) return body;
    return name && body ? name + '\n' + body : name || body;
  }

  /** Which of the two is the record — the one a sheet was OPENED on, or the one
      it last COMMITTED. Answer: the commit, whenever it is the same record.

      ── WHY THERE ARE EVER TWO ─────────────────────────────────────────────
      A sheet seeds its text once, at mount, from the record it was handed. That
      is exactly right for opening a record and wrong for opening it a second
      time, and the second time is not hypothetical: a record is drawn inside the
      day panel on a narrow window and out on the canvas on a wide one, which are
      two places in the tree, so crossing that width unmounts and remounts it.
      Seeded again from the record as it was OPENED, the sheet's next commit
      writes that text back over everything written since. Measured, in the
      browser, on real writing.

      The commit is the record as it actually stands, so the commit is what a
      second mount has to start from. The uid check is what stops the cure being
      worse than the disease: answering with a DIFFERENT record would write one
      record's writing into another, and no staleness is worth that. */
  function mdRecordLatest(opened: unknown, committed: unknown): unknown {
    const isRec = (r: unknown) => !!r && typeof r === 'object';
    if (!isRec(committed)) return isRec(opened) ? opened : null;
    if (!isRec(opened)) return committed;
    const a = (opened as { uid?: unknown }).uid;
    const b = (committed as { uid?: unknown }).uid;
    return a && b && a === b ? committed : opened;
  }

  /* Identity for a new record. Three parts, and each answers a different way two
     records could end up sharing one uid — which does not merely look wrong, it
     merges two records into one, on every device.
       • the time, first, so uids sort in creation order;
       • a counter, because records are created by OPENING one now and two
         openings can land in the same millisecond (a fast "+" after a discard,
         a drag on the grid). Within this session that is decided, not hoped;
       • randomness, because the counter restarts on every device and two of
         them writing in the same millisecond would otherwise agree. */
  let mdRecordSeq = 0;
  function mdRecordUid(): string {
    return 'r' + Date.now().toString(36) + (mdRecordSeq++).toString(36)
      + Math.random().toString(36).slice(2, 5);
  }

  /* ── the ref: what names a record wherever it lives ───────────────────────
     Three shapes are refused, and each is a real failure rather than tidiness:

       • a malformed id, because a ref is written into a board document that
         syncs, and server/sync.js matches keys against a pattern — the same
         reason a board id is lowercase alphanumeric (see md-board.ts);
       • a `:` anywhere in a part, because the key form joins on it and one
         stray colon turns `mdRecordRefParse` into a different ref rather than
         a failure — silent, and pointing at somebody else's writing;
       • a day that is not a calendar day, because a ref carrying a bad day
         would be looked up in a day map that can never have that key.

     What is NOT refused is a type this build does not know. See the header. */
  const MD_REF_TYPE = /^[a-z][a-z0-9]{0,15}$/;
  const MD_REF_ID = /^[a-z0-9]{1,64}$/;
  const MD_REF_DAY = /^\d{4}-\d{2}-\d{2}$/;

  /** What a record with no `type` on it is. Never written — absent means this.

      Writing `type:'note'` onto the records that already exist would change
      every one of their content keys (mdContentKey walks every key on a
      record), re-stamp every one of them, and push every day document the user
      has ever written. Absent is free; a default is not. */
  const MD_RECORD_DEFAULT_TYPE = 'note';

  /** Build a ref, or null when it would not name anything. */
  function mdRecordRef(type: unknown, id: unknown, day?: unknown): MdRecordRef | null {
    if (typeof type !== 'string' || !MD_REF_TYPE.test(type)) return null;
    /* Craft is the canonical identity. Older boards and shared links used the
       pre-rename `manifest` type; accepting it here performs the migration at
       the narrowest boundary and every subsequent serialization writes craft. */
    const canonicalType = type === 'manifest' ? 'craft' : type;
    if (typeof id !== 'string' || !MD_REF_ID.test(id)) return null;
    if (day == null || day === '') return { type: canonicalType, id };
    if (typeof day !== 'string' || !MD_REF_DAY.test(day)) return null;
    return { type: canonicalType, id, day };
  }

  /** What a record says its type is. */
  function mdRecordType(rec: unknown): string {
    const t = rec && typeof rec === 'object' ? (rec as { type?: unknown }).type : null;
    return typeof t === 'string' && MD_REF_TYPE.test(t) ? t : MD_RECORD_DEFAULT_TYPE;
  }

  /** The ref for a record sitting in a day. */
  function mdRecordRefFor(rec: unknown, day: unknown): MdRecordRef | null {
    if (!rec || typeof rec !== 'object') return null;
    return mdRecordRef(mdRecordType(rec), (rec as { uid?: unknown }).uid, day);
  }

  /** One string, so a ref can be a board element's field or a map key. */
  function mdRecordRefKey(ref: unknown): string {
    const r = ref as MdRecordRef | null;
    if (!r || typeof r !== 'object') return '';
    const ok = mdRecordRef(r.type, r.id, r.day);
    if (!ok) return '';
    return ok.day ? `${ok.type}:${ok.day}:${ok.id}` : `${ok.type}:${ok.id}`;
  }

  /** …and back again. Round-trips with mdRecordRefKey for every valid ref. */
  function mdRecordRefParse(key: unknown): MdRecordRef | null {
    if (typeof key !== 'string' || !key) return null;
    const parts = key.split(':');
    if (parts.length === 2) return mdRecordRef(parts[0], parts[1]);
    if (parts.length === 3) return mdRecordRef(parts[0], parts[2], parts[1]);
    return null;
  }

  /** Do these two name the same record? Null never equals anything, including null. */
  function mdRecordRefEq(a: unknown, b: unknown): boolean {
    const ka = mdRecordRefKey(a);
    return !!ka && ka === mdRecordRefKey(b);
  }

  /* ── the day a record came from ──────────────────────────────────────────
     EVERY RECORD ALWAYS HAS A DAY (Marwan, 2026-08-18): the day somebody
     scheduled it for if they chose one, and otherwise the day it was written.
     A thought captured on a Tuesday DID happen on that Tuesday — that is a
     fact about it, not a blank waiting to be filled — so the board can say
     when, the timeline can draw it, and there is always a day for its tick to
     live in.

     IT IS WRITTEN DOWN, NOT WORKED OUT. `created` is an instant, and turning
     an instant into a day requires a timezone: derive it on read and a line
     written at 23:40 in Cairo becomes the next day for a reader in New York.
     The day a record came from is the day ITS WRITER was living in, so it is
     stamped at creation, in the writer's own local time, and never touched
     again. Scheduling does not revise it; filing it under another project does
     not revise it. It is history, and history is not a field.

     A RECORD WRITTEN BEFORE THIS BUILD still answers, from `created`, exactly
     as md-worklist.ts has been answering all along. Nothing is backfilled: the
     fallback IS the migration, the same bargain absence-is-Inbox strikes one
     floor up.

     RECORDED, NOT FILED. An unscheduled record does not appear in the day it
     came from. The backlog is a real place and this does not empty it back
     across the calendar. */
  const MD_DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

  const pad2 = (value: number): string => (value < 10 ? `0${value}` : String(value));

  /** The local day one instant fell on — the day somebody was living in, which
      is what mdDayKey means by a day everywhere else in this app. */
  function dayOfStamp(ms: unknown): string | null {
    if (typeof ms !== 'number' || !Number.isFinite(ms) || ms <= 0) return null;
    const on = new Date(ms);
    return `${on.getFullYear()}-${pad2(on.getMonth() + 1)}-${pad2(on.getDate())}`;
  }

  /** What a door stamps on a record it is making. One helper for all of them:
      five call sites each spelling out a date is how two records written in the
      same minute end up claiming different days. */
  function mdRecordOriginStamp(): string {
    return dayOfStamp(Date.now()) as string;
  }

  /** The day a record came from. Written down beats worked out; a word that is
      not a day is not an answer, so it falls through to the timestamp rather
      than being passed on to a caller that will try to render it. */
  function mdRecordOriginDay(record: unknown): string | null {
    if (!record || typeof record !== 'object') return null;
    const row = record as Record<string, unknown>;
    if (typeof row.origin === 'string' && MD_DAY_RE.test(row.origin)) return row.origin;
    return dayOfStamp(row.created);
  }

  /* ── a record's status ────────────────────────────────────────────────────
     ONE VOCABULARY, ALL THE WAY DOWN. This file used to write out four words of
     its own — backlog · active · parked · done — because it loads before
     md-workflow.ts and a list reached for at load time would have been a silent
     `undefined` in whichever screen booted first. The list is gone (WR-3): a
     record stands on a status of the workflow its project resolves to, and the
     workflow is asked for at CALL time, which has never depended on load order.

     ── THE TICK AND THE WORD ARE ONE FACT ────────────────────────────────────
     Every record has a day to be ticked in (WR-1) — the day somebody scheduled
     it for, or the day it was written. So the old bargain of "two homes, one
     meaning" has nothing left to divide: the word is the truth, and the tick is
     how the day says it.

     A TICK MEANS THE LAST STATUS IN COMPLETED (Marwan, 2026-08-18). Moving a
     card to that status is the same act as ticking it in the morning, so moving
     it there stores NOTHING and ticks the day — which is what lets unticking
     put the card back where it came from rather than dropping it into Inbox.
     A workflow with a second, earlier Completed status is the one case where a
     word is stored alongside the tick, because that word says more than the
     tick does. */

  /** The day a record's tick is keyed by: the day somebody CHOSE, or the day it
      was written. `scheduled` is what the caller knows — a day record's day is
      the document it lives in, never a field on the row. */
  function mdRecordTickDay(record: unknown, scheduled?: unknown): string | null {
    if (typeof scheduled === 'string' && MD_DAY_RE.test(scheduled)) return scheduled;
    return mdRecordOriginDay(record);
  }

  /** What moving one record to `want` has to write. Returns `null` for a word
      that is not a status of this record's workflow; otherwise an object whose
      PRESENT keys are the writes and whose absent keys are the things to leave
      alone:

        `status` absent  — do not touch the stored word
        `status: null`   — clear it
        `status: 'x'`    — store it
        `tick` absent    — do not touch the day's tick
        `tick: true`     — tick it off in its day
        `tick: false`    — lift the tick

      Two rules do all the work, and both are about writing as little as
      possible. The word is CLEARED rather than stored when it is the workflow's
      Inbox, because that is where absence already resolves to — storing it
      would put a byte in a document to say what the byte's absence says. And
      the workflow's FINISH is the tick alone, with the stored word underneath
      left exactly as it was, so an untick has somewhere to put the card back
      to. */
  function mdRecordStatusNext(record: unknown, scheduled: unknown, want: unknown):
  { status?: string | null; tick?: boolean } | null {
    const flow = g.mdWorkflowForRecord ? g.mdWorkflowForRecord(record) : null;
    if (!flow || !g.mdWorkflowStatusById) return null;
    const status = g.mdWorkflowStatusById(flow.id, want);
    if (!status) return null;
    const on = mdRecordTickDay(record, scheduled);
    if (status.category === 'completed') {
      const finish = g.mdWorkflowFinishOf ? g.mdWorkflowFinishOf(flow.id) : null;
      const terminal = !!(finish && finish.id === status.id);
      /* No day to be ticked in — a record with neither a chosen day nor a
         written one, which only a hand-built row can be — so the word has to
         carry the whole fact by itself. */
      if (!on) return { status: status.id };
      if (!terminal) return { tick: true, status: status.id };
      /* A word already stored FROM the Completed category outranks the tick on
         the way back out — it says more than the tick does — so asking for the
         finish has to clear it, or the card would refuse to move the last step
         of its own workflow. Nothing else is cleared: an ordinary word
         underneath is what an untick puts the card back to. */
      const stood = g.mdRecordStatusOf ? g.mdRecordStatusOf(record, scheduled, null) : null;
      return stood && stood.category === 'completed' && stood.id !== status.id
        ? { tick: true, status: null } : { tick: true };
    }
    const inbox = g.mdWorkflowInboxOf ? g.mdWorkflowInboxOf(flow.id) : null;
    const home = !!(inbox && inbox.id === status.id);
    return on ? { status: home ? null : status.id, tick: false }
      : { status: home ? null : status.id };
  }

  /** Carry `mdRecordStatusNext`'s `tick` into the completions map.

      md-app's `completed` is one flat map keyed `${day}|${uid}`, so a tick is
      the presence of a key and untick is its ABSENCE — storing `false` would
      leave a byte in the document for every record anybody has ever unticked.

      THE SAME MAP COMES BACK WHEN NOTHING MOVED, because md-app compares state
      by identity: a move that changes nothing must not re-render the day and
      re-persist a map it did not touch. `tick` undefined means the move said
      nothing about ticks. */
  function mdRecordStatusTick(completed: unknown, day: unknown, uid: unknown,
    tick: unknown): Record<string, unknown> {
    const held = (completed && typeof completed === 'object'
      ? completed : {}) as Record<string, unknown>;
    if (typeof tick !== 'boolean') return held;
    if (typeof day !== 'string' || !day || typeof uid !== 'string' || !uid) return held;
    const key = `${day}|${uid}`;
    if (!!held[key] === tick) return held;
    const next = { ...held };
    if (tick) next[key] = true; else delete next[key];
    return next;
  }

  /** Move a tick from one day's key to another's, for a record that has just
      been given a day.

      SCHEDULING MUST NOT UN-COMPLETE ANYTHING. A finished thought sitting in
      the backlog is ticked under the day it was WRITTEN; putting it on next
      Tuesday re-keys everything that answers for it, and a tick left behind on
      the old key would take the card out of Completed the moment somebody
      scheduled it — and quietly leave a completion in a day the record is not
      in. One move, both keys, so there is no instant where it is neither. */
  function mdRecordStatusCarryTick(completed: unknown, from: unknown, to: unknown,
    uid: unknown): Record<string, unknown> {
    const held = (completed && typeof completed === 'object'
      ? completed : {}) as Record<string, unknown>;
    if (typeof uid !== 'string' || !uid) return held;
    if (typeof from !== 'string' || !from || typeof to !== 'string' || !to) return held;
    if (from === to) return held;
    if (!held[`${from}|${uid}`]) return held;
    const next = { ...held };
    delete next[`${from}|${uid}`];
    next[`${to}|${uid}`] = true;
    return next;
  }

  /** Whether work on `day` at `min` for `dur` minutes has ALREADY PASSED.

      "Time given" is the one number in this app that cannot fall, and that is
      only true if a session running right now counts for nothing: counting it
      early makes the number tick upward while somebody watches it, which is the
      one thing a calm metric must not do. Scheduling six hours next week must
      not read as six hours given today, either.

      ONE RULE, ONE HOME. The Workshop counts a project's hours and the worklist
      counts a record's; those are two questions with the same arithmetic, and
      two copies of arithmetic is how two screens start disagreeing about the
      same afternoon. (md-project.ts keeps an older, slightly different variant
      for mdProjectStats — same shape, different answer for an all-day record.
      Reconciling those two answers is a decision about what a day-long record
      is worth, not a refactor, so it is left alone here rather than changed by
      accident.)

      An all-day record on TODAY has passed: it had the whole day, and there is
      no hour to still be inside of. */
  function mdRecordHasPassed(day: unknown, min: unknown, dur: unknown,
    todayKey: unknown, nowMin: unknown): boolean {
    if (typeof day !== 'string' || !day) return false;
    const today = typeof todayKey === 'string' ? todayKey : '';
    if (!today) return true;                     // no clock handed in; nothing is ahead
    if (day < today) return true;
    if (day > today) return false;
    if (typeof min !== 'number' || !Number.isFinite(min)) return true;   // all-day, today
    const now = typeof nowMin === 'number' && Number.isFinite(nowMin) ? nowMin : 0;
    const length = typeof dur === 'number' && Number.isFinite(dur) && dur > 0 ? dur : 0;
    return min + length <= now;
  }

  g.MD_RECORD_PLACEHOLDER = MD_RECORD_PLACEHOLDER;
  g.mdRecordOriginStamp = mdRecordOriginStamp;
  g.mdRecordOriginDay = mdRecordOriginDay;
  g.mdRecordTickDay = mdRecordTickDay;
  g.mdRecordStatusNext = mdRecordStatusNext;
  g.mdRecordStatusTick = mdRecordStatusTick;
  g.mdRecordStatusCarryTick = mdRecordStatusCarryTick;
  g.mdRecordHasPassed = mdRecordHasPassed;
  g.MD_RECORD_DEFAULT_TYPE = MD_RECORD_DEFAULT_TYPE;
  g.mdRecordParts = mdRecordParts;
  g.mdRecordIsBlank = mdRecordIsBlank;
  g.mdRecordTitle = mdRecordTitle;
  g.mdRecordSeedText = mdRecordSeedText;
  g.mdRecordLatest = mdRecordLatest;
  g.mdRecordUid = mdRecordUid;
  g.mdRecordRef = mdRecordRef;
  g.mdRecordType = mdRecordType;
  g.mdRecordRefFor = mdRecordRefFor;
  g.mdRecordRefKey = mdRecordRefKey;
  g.mdRecordRefParse = mdRecordRefParse;
  g.mdRecordRefEq = mdRecordRefEq;

  // CommonJS escape hatch, following md-link.ts and md-merge.ts.
  const mod = (globalThis as { module?: { exports?: unknown } }).module;
  if (mod && mod.exports) {
    mod.exports = { MD_RECORD_PLACEHOLDER, MD_RECORD_DEFAULT_TYPE,
      mdRecordOriginStamp, mdRecordOriginDay,
      mdRecordParts, mdRecordIsBlank, mdRecordTickDay, mdRecordStatusNext,
      mdRecordStatusTick, mdRecordStatusCarryTick,
      mdRecordHasPassed,
      mdRecordTitle, mdRecordSeedText, mdRecordLatest, mdRecordUid, mdRecordRef, mdRecordType, mdRecordRefFor,
      mdRecordRefKey, mdRecordRefParse, mdRecordRefEq };
  }
})(typeof window !== 'undefined' ? window : globalThis);
